import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  OutlinedInput,
  ListItemText,
  Checkbox
} from '@mui/material';
import { robojacketsTheme } from '../../theme/robojacketsTheme';
import { hasAnyRole } from '../../utils/auth';

const GroupForm = ({ 
  open, 
  onClose, 
  group, 
  onSave, 
  mode = 'create',
  groupTypes = [],
  users = [],
  currentUser
}) => {
  // Filter group types based on user role
  const getFilteredGroupTypes = () => {
    if (!currentUser) return groupTypes;
    
    // If user is Team Supervisor (and doesn't have higher privileges), only show FTC team types
    if (currentUser.role === 'team_supervisor') {
      return groupTypes.filter(type => 
        type.name && type.name.match(/FTC-.*-Team/)
      );
    }
    
    // For Coach, Staff, and other roles, show all group types
    return groupTypes;
  };

  // Check if user can edit group members (Staff or Coach only)
  const canEditMembers = () => {
    return hasAnyRole(['staff', 'coach']);
  };

  // Check if user can edit primary user (Staff, Coach, or Team Supervisor who is the primary user)
  const canEditPrimaryUser = () => {
    if (hasAnyRole(['staff', 'coach'])) {
      return true;
    }
    
    // Team Supervisors can only edit primary user if they are the current primary user
    if (currentUser?.role === 'team_supervisor' && mode === 'edit' && group) {
      return group.primaryUserId === currentUser.id;
    }
    
    return false;
  };
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true,
    primaryUserId: null,
    seekingMembers: false,
    groupTypeId: null,
    userIds: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (group && mode === 'edit') {
      setFormData({
        name: group.name || '',
        code: group.code || '',
        description: group.description || '',
        isActive: group.isActive !== undefined ? group.isActive : true,
        primaryUserId: group.primaryUserId || null,
        seekingMembers: group.seekingMembers || false,
        groupTypeId: group.groupTypeId || null,
        userIds: group.userIds || []
      });
    } else {
      // For create mode, set creator as sole member and primary user
      const creatorId = currentUser?.id;
      setFormData({
        name: '',
        code: '',
        description: '',
        isActive: true,
        primaryUserId: creatorId || null,
        seekingMembers: false,
        groupTypeId: null,
        userIds: creatorId ? [creatorId] : []
      });
    }
    setErrors({});
  }, [group, mode, open, currentUser]);

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' || field === 'seekingMembers' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleUserChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      userIds: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Name must be 50 characters or less';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (formData.code.length > 20) {
      newErrors.code = 'Code must be 20 characters or less';
    }
    
    if (formData.description && formData.description.length > 255) {
      newErrors.description = 'Description must be 255 characters or less';
    }
    
    if (!formData.groupTypeId) {
      newErrors.groupTypeId = 'Group type is required';
    }
    
    if (mode === 'edit' && formData.primaryUserId && !formData.userIds.includes(formData.primaryUserId)) {
      newErrors.primaryUserId = 'Primary user must be a member of the group';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      isActive: true,
      primaryUserId: null,
      seekingMembers: false,
      groupTypeId: null,
      userIds: []
    });
    setErrors({});
    onClose();
  };

  const getUserDisplayName = (user) => {
    const preferredName = user.preferredName || user.firstName;
    const lastName = user.lastName || '';
    const graduationYear = user.graduationYear || '';
    return `${preferredName} ${lastName} ${graduationYear}`.trim();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle 
        sx={{
          backgroundColor: robojacketsTheme.palette.primary.main,
          color: 'white',
          fontWeight: 'bold'
        }}
      >
        {mode === 'create' ? 'Create Group' : 'Edit Group'}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 6 }}>
        <Box sx={{ mb: 3 }}>
                      <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2, mt: 2 }}
            />
            
            <TextField
              fullWidth
              label="Code (Team Number)"
              value={formData.code}
              onChange={handleChange('code')}
              error={!!errors.code}
              helperText={errors.code || "Enter a unique code for this group (e.g., team number)"}
              sx={{ mb: 2 }}
            />
          
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          
                      <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.groupTypeId}>
              <InputLabel>Group Type</InputLabel>
              <Select
                value={formData.groupTypeId || ''}
                onChange={handleChange('groupTypeId')}
                label="Group Type"
              >
                {getFilteredGroupTypes().map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.groupTypeId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.groupTypeId}
                </Typography>
              )}
            </FormControl>
          
          {mode === 'edit' && canEditMembers() && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Members</InputLabel>
              <Select
                multiple
                value={formData.userIds}
                onChange={handleUserChange}
                input={<OutlinedInput label="Members" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const user = users.find(u => u.id === userId);
                      return user ? (
                        <Chip key={userId} label={getUserDisplayName(user)} />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Checkbox checked={formData.userIds.indexOf(user.id) > -1} />
                    <ListItemText primary={getUserDisplayName(user)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {mode === 'edit' && canEditPrimaryUser() && (
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.primaryUserId}>
              <InputLabel>Primary User</InputLabel>
              <Select
                value={formData.primaryUserId || ''}
                onChange={handleChange('primaryUserId')}
                label="Primary User"
              >
                <MenuItem value="">
                      <em>None</em>
                </MenuItem>
                {formData.userIds.map((userId) => {
                  const user = users.find(u => u.id === userId);
                  return user ? (
                    <MenuItem key={userId} value={userId}>
                      {getUserDisplayName(user)}
                    </MenuItem>
                  ) : null;
                })}
              </Select>
              {errors.primaryUserId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.primaryUserId}
                </Typography>
              )}
            </FormControl>
          )}
          
          {mode === 'edit' && !canEditMembers() && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="body2" color="info.main">
                <strong>Member Management:</strong> Only Staff and Coach roles can directly edit group members. 
                Team Supervisors must use the application process to add members to their groups.
              </Typography>
            </Box>
          )}
          
          {mode === 'edit' && !canEditPrimaryUser() && canEditMembers() && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="body2" color="warning.main">
                <strong>Primary User Management:</strong> Only Staff and Coach roles can change the primary user of any group.
              </Typography>
            </Box>
          )}
          
          {mode === 'create' && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Group Creator:</strong> {currentUser?.name || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You will be automatically added as the sole member and primary user of this group.
              </Typography>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.seekingMembers}
                onChange={handleChange('seekingMembers')}
                color="primary"
              />
            }
            label="Seeking Members"
            sx={{ mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange('isActive')}
                color="primary"
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{
            backgroundColor: robojacketsTheme.palette.primary.main,
            '&:hover': {
              backgroundColor: robojacketsTheme.palette.primary.dark
            }
          }}
        >
          {mode === 'create' ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupForm; 